import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { historyShape } from 'constants/propTypes';
import { Column } from 'components/shared/Layout';
import moment from 'moment';
import { parse } from 'query-string';
import { DateSearch } from '@eurostar/ui-components';
import { OUTBOUND_DATE, INBOUND_DATE, OUTBOUND, INBOUND, BOOKING_ITEMS } from 'constants/booking';
import { localeToMarket } from 'utils/locale';
import { StyledJourneySearchDate, StyledDateSearch } from './JourneySearchDate.styles';
import { getMinDateBookingPath, getMinDateExchange, getMaxDate } from './JourneySearchDate.utils';
import { getProductsFromItemRefs } from './JourneySearchDate.utils';
import { productContainSubscriptionFare } from 'utils/subscriptionFares';
import { getNSIndividualExchangeDate } from 'utils/summatedFares';

const EXCHANGE_PATH = '/exchange/';

const JourneySearchDate = ({
  dateToChange,
  oppositeJourney,
  booking,
  history: {
    location: { pathname, search, state },
    push
  },
  intl: { locale },
  isReturnJourney,
  currency,
  cheapestFares,
  subscriptionStartDate,
  subscriptionEndDate,
  leg = [],
  isExchangingSingleLegOfSNCBSummated,
  testIdSuffix = ''
}) => {
  const isExchange = pathname.includes(EXCHANGE_PATH);
  const date = parse(search)[dateToChange];
  const outboundDate = parse(search)[OUTBOUND_DATE];
  const inboundDate = parse(search)[INBOUND_DATE];
  const direction = dateToChange === OUTBOUND_DATE ? OUTBOUND : INBOUND;
  const selectedDate = date ? new Date(date) : new Date();

  const firstSelectedItemRef = (parse(search)[BOOKING_ITEMS] || '').split(',')[0];
  const selectedItems = (parse(search)[BOOKING_ITEMS] || '').split(',');
  const productsFromItemRefs = getProductsFromItemRefs({ segments: leg, selectedItemRefs: selectedItems });
  const hasSubscriptionFare = parse(search)[BOOKING_ITEMS] ? productContainSubscriptionFare({ products: productsFromItemRefs }) : true;

  const paxId = booking?.allBookingItems?.find(({ itemRef }) => itemRef === firstSelectedItemRef)?.passenger.id;
  const passengerOppositeJourney = oppositeJourney?.find(({ products }) =>
    products.some(({ passengerId, cancelled }) => !cancelled && passengerId === paxId)
  );

  let minDate = isExchange
    ? getMinDateExchange(
        dateToChange,
        outboundDate,
        isReturnJourney,
        subscriptionStartDate,
        direction,
        passengerOppositeJourney,
        hasSubscriptionFare,
        isExchangingSingleLegOfSNCBSummated,
        inboundDate
      )
    : getMinDateBookingPath(dateToChange, outboundDate, isReturnJourney);
  let maxDate = getMaxDate(
    dateToChange,
    isExchange,
    inboundDate,
    subscriptionEndDate,
    direction,
    passengerOppositeJourney,
    hasSubscriptionFare,
    isExchangingSingleLegOfSNCBSummated,
    outboundDate
  );

  const nsExchangeDate = isExchange && getNSIndividualExchangeDate({ segment: leg, selectedItemRefs: selectedItems });
  if (nsExchangeDate) {
    minDate = moment.max(minDate, moment(nsExchangeDate));
    const lastAllowedDate = moment(nsExchangeDate).add(1, 'days');
    maxDate = maxDate ? moment.min(maxDate, lastAllowedDate) : lastAllowedDate;
    if (hasSubscriptionFare && subscriptionStartDate) minDate = moment.max(minDate, moment(subscriptionStartDate));
    if (hasSubscriptionFare && subscriptionEndDate) maxDate = moment.min(maxDate, moment(subscriptionEndDate));
  }

  const onDateChange = (newDate) => {
    const newSelectedDate = moment(newDate).format('YYYY-MM-DD');
    if (dateToChange === OUTBOUND_DATE && !!inboundDate) {
      const shouldChangeInBoundDate = moment(newDate).isAfter(moment(inboundDate));

      const newSearch = shouldChangeInBoundDate
        ? search
            .replace(`${OUTBOUND_DATE}=${date}`, `${OUTBOUND_DATE}=${newSelectedDate}`)
            .replace(`${INBOUND_DATE}=${inboundDate}`, `${INBOUND_DATE}=${newSelectedDate}`)
        : search.replace(`${OUTBOUND_DATE}=${date}`, `${OUTBOUND_DATE}=${newSelectedDate}`);

      return push({
        pathname,
        search: newSearch,
        state
      });
    }

    return push({
      pathname,
      search: search.replace(`${dateToChange}=${date}`, `${dateToChange}=${newSelectedDate}`),
      state
    });
  };

  const styledDateSearchRef = useRef();

  // This has to be done to the element directly to avoid re-rendering the DateSearch
  const setSelectedDateOffset = (numberNavigated = 0) => {
    const selectedDateOffset = parseInt(styledDateSearchRef?.current?.getAttribute('data-date-offset') || '0');
    styledDateSearchRef?.current?.setAttribute('data-date-offset', selectedDateOffset + numberNavigated);
  };

  return (
    <StyledJourneySearchDate>
      <Column alignItems="center" justifyContent="flex-start" flexDirection="row">
        <StyledDateSearch isReturnJourney={isReturnJourney} ref={styledDateSearchRef} data-date-offset="0">
          <DateSearch
            testId={`${direction}${testIdSuffix}`}
            direction={direction}
            selectedDate={selectedDate}
            minDate={minDate}
            maxDate={maxDate}
            onChange={(newDate) => onDateChange(newDate)}
            onNavigateChange={(numberNavigated) => setSelectedDateOffset(numberNavigated)}
            market={localeToMarket(locale)}
            cheapestFares={cheapestFares?.cheapestFares}
            currency={currency}
          />
        </StyledDateSearch>
      </Column>
    </StyledJourneySearchDate>
  );
};

JourneySearchDate.propTypes = {
  booking: PropTypes.shape({
    allBookingItems: PropTypes.array
  }),
  oppositeJourney: PropTypes.array,
  history: historyShape,
  dateToChange: PropTypes.string,
  intl: PropTypes.shape({
    locale: PropTypes.string
  }),
  isReturnJourney: PropTypes.bool,
  cheapestFares: PropTypes.shape({
    cheapestFares: PropTypes.arrayOf(
      PropTypes.shape({
        date: PropTypes.string,
        price: PropTypes.number
      })
    )
  }),
  currency: PropTypes.string,
  subscriptionStartDate: PropTypes.string,
  subscriptionEndDate: PropTypes.string,
  leg: PropTypes.array,
  isExchangingSingleLegOfSNCBSummated: PropTypes.bool,
  testIdSuffix: PropTypes.string
};

export default JourneySearchDate;
